import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { Sparkles, Clock, CheckCircle2, CreditCard, Download, AlertCircle, Loader2 } from "lucide-react";
import PoweredBy from "../components/PoweredBy";

const STATUS_LABEL = {
  pending:     { label: "En attente de proposition", color: "#9CA3AF", icon: Clock,        step: 1 },
  accepted:    { label: "Brief accepté",              color: "#FBBF24", icon: CheckCircle2, step: 2 },
  in_progress: { label: "Création en cours",          color: "#D4AF37", icon: Sparkles,    step: 3 },
  delivered:   { label: "Livré - En attente paiement", color: "#10B981", icon: CreditCard, step: 4 },
  paid:        { label: "Payé - Téléchargement ouvert", color: "#10B981", icon: Download,  step: 5 },
  cancelled:   { label: "Annulé",                      color: "#EF4444", icon: AlertCircle, step: 0 },
};

export default function BriefTrack() {
  const { token } = useParams();
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBrief = async () => {
    try {
      const { data } = await api.get(`/agency/brief/track/${token}`);
      setBrief(data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Brief introuvable");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchBrief();
    const t = setInterval(fetchBrief, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [token]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>;
  if (error) return <div className="min-h-[60vh] flex items-center justify-center"><div className="text-center"><AlertCircle className="w-10 h-10 text-red-400 mx-auto" /><p className="mt-3 text-white/70">{error}</p></div></div>;

  const info = STATUS_LABEL[brief.status] || STATUS_LABEL.pending;
  const Icon = info.icon;
  const downloadDeliverable = () => {
    if (!brief.deliverable?.data_url) return;
    const a = document.createElement("a");
    a.href = brief.deliverable.data_url;
    a.download = `livraison-${brief.brief_id}.${brief.deliverable.kind === "flyer" ? "png" : "mp4"}`;
    a.click();
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="text-center mb-8">
        <span className="label-mono">Suivi de brief</span>
        <h1 className="mt-2 font-heading text-3xl tracking-tighter">Votre commande chez <span className="gold-text">{brief.agency_name}</span></h1>
      </div>

      {/* Timeline */}
      <div className="studio-card p-6 mb-5">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/30 flex items-center justify-center" style={{color: info.color}}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading text-xl" data-testid="brief-status">{info.label}</div>
            <div className="text-xs text-white/40">Brief #{brief.brief_id.slice(-8)}</div>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs">
          {[1, 2, 3, 4, 5].map(n => (
            <div key={n} className="flex-1 flex flex-col items-center">
              <div className={`w-3 h-3 rounded-full ${n <= info.step ? "bg-gold" : "bg-white/10"}`} />
              {n < 5 && <div className={`h-0.5 w-full -mt-2 ${n < info.step ? "bg-gold" : "bg-white/10"}`} style={{transform: "translateY(-3px)"}} />}
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-white/40 mt-2 uppercase tracking-widest">
          <span>Reçu</span><span>Accepté</span><span>En cours</span><span>Livré</span><span>Payé</span>
        </div>
      </div>

      {/* Brief details */}
      <div className="studio-card p-6 mb-5">
        <div className="label-mono mb-3">Votre demande</div>
        <div className="text-sm text-white/80">{brief.prompt}</div>
        {brief.details && <div className="mt-3 text-xs text-white/50">{brief.details}</div>}
        <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
          <div><div className="text-white/40">Type</div><div className="capitalize">{brief.request_type}</div></div>
          <div><div className="text-white/40">Créé le</div><div>{new Date(brief.created_at).toLocaleDateString("fr-FR")}</div></div>
        </div>
      </div>

      {/* Deliverable + payment */}
      {brief.status === "delivered" && brief.payment_url && (
        <div className="gold-border-gradient p-6 mb-5">
          <h3 className="font-heading text-2xl">Votre pub est prête</h3>
          <p className="text-white/70 mt-2 text-sm">Validez le paiement pour accéder à la version finale HD sans filigrane.</p>
          {brief.price && <div className="mt-4 text-3xl font-heading gold-text font-semibold">{brief.price.toFixed(0)} €</div>}
          {brief.deliverable?.thumbnail_url && (
            <img src={brief.deliverable.thumbnail_url} alt="aperçu" className="mt-4 max-h-60 rounded-lg border border-white/10 mx-auto" />
          )}
          <a href={brief.payment_url} data-testid="pay-brief-btn" className="gold-btn mt-6 inline-flex">
            <CreditCard className="w-4 h-4" /> Régler {brief.price?.toFixed(0)} €
          </a>
        </div>
      )}

      {brief.status === "paid" && brief.deliverable?.data_url && (
        <div className="gold-border-gradient p-6 mb-5 text-center">
          <CheckCircle2 className="w-10 h-10 text-gold mx-auto" />
          <h3 className="font-heading text-2xl mt-3">Paiement confirmé</h3>
          <p className="text-white/70 mt-2 text-sm">Téléchargez votre pub finale ci-dessous.</p>
          {brief.deliverable.kind === "flyer" ? (
            <img src={brief.deliverable.data_url} alt="livraison" className="mt-4 max-h-96 rounded-lg border border-gold/30 mx-auto" />
          ) : (
            <video src={brief.deliverable.data_url} controls className="mt-4 max-h-96 mx-auto rounded-lg" />
          )}
          <button onClick={downloadDeliverable} data-testid="download-deliverable" className="gold-btn mt-5 inline-flex">
            <Download className="w-4 h-4" /> Télécharger
          </button>
        </div>
      )}

      <p className="text-center text-xs text-white/30 mt-8">Cette page se rafraîchit automatiquement toutes les 8 secondes.</p>

      <PoweredBy variant="inline" />
    </div>
  );
}
